import type common from './locales/en/common.json';
import type { ResourceBundle } from './types';

/**
 * Every locale i18next may resolve to. Generic `en` is load-bearing here: it is
 * the canonical bundle, the `fallbackLng`, and what a browser reporting bare
 * `en` — or an unsupported English region such as `en-AU` — resolves to. Do not
 * narrow this list to restrict the picker; narrow `selectableLocales()` instead.
 */
export const SUPPORTED_LOCALES = ['en', 'en-GB', 'en-US'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export type CommonResource = typeof common;

/**
 * The regional locales a user may choose between. Generic English is a
 * canonical source and a fallback, never a product choice, so it is not offered.
 */
export const SELECTABLE_LOCALES = ['en-GB', 'en-US'] as const satisfies readonly SupportedLocale[];

/**
 * Locales a locale picker should offer. Generic `en` is exposed only on local
 * development, where being able to inspect the canonical bundle directly is
 * useful; staging and production offer British and American English only.
 *
 * Callers supply the environment themselves (e.g.
 * `selectableLocales({ includeGeneric: getEnvironment() === 'local' })`) so this
 * package stays free of host-sniffing.
 */
export function selectableLocales(options: {
  includeGeneric: boolean;
}): readonly SupportedLocale[] {
  return options.includeGeneric ? SUPPORTED_LOCALES : SELECTABLE_LOCALES;
}

/**
 * The locale a picker should show as currently selected.
 *
 * When the active locale is not on offer — a production visitor whose browser
 * reported bare `en` — British is the honest substitute: `en-GB` restates the
 * regionally-differing keys with the same British values `en` holds and inherits
 * the rest by fallback, so the user is already reading British English.
 *
 * The result is always drawn from `offered` so a picker can never be handed a
 * value outside its own options. `en-GB` is preferred when offered, otherwise
 * the first offered locale wins. An empty `offered` list has no valid answer;
 * `en-GB` is returned so callers still render a defined value.
 */
export function resolvePickerLocale(
  language: string | undefined,
  offered: readonly SupportedLocale[],
): SupportedLocale {
  const resolved = resolveSupportedLocale(language);
  if (offered.includes(resolved)) return resolved;
  if (offered.includes('en-GB')) return 'en-GB';
  return offered[0] ?? 'en-GB';
}

const COMMON_RESOURCE_LOADERS: Record<
  SupportedLocale,
  () => Promise<{ default: ResourceBundle }>
> = {
  en: () => import('./locales/en/common.json'),
  'en-GB': () => import('./locales/en-GB/common.json'),
  'en-US': () => import('./locales/en-US/common.json'),
};

function isResourceBundle(value: unknown): value is ResourceBundle {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeResourceBundles(base: ResourceBundle, override: ResourceBundle): ResourceBundle {
  const result = { ...base };

  for (const [key, value] of Object.entries(override)) {
    const baseValue = result[key];
    result[key] =
      isResourceBundle(baseValue) && isResourceBundle(value)
        ? mergeResourceBundles(baseValue, value)
        : value;
  }

  return result;
}

export function resolveSupportedLocale(language: string | undefined): SupportedLocale {
  const normalized = language?.toLowerCase();
  if (normalized === 'en-gb') return 'en-GB';
  if (normalized === 'en-us') return 'en-US';
  return 'en';
}

export async function loadCommonResource(language: string): Promise<ResourceBundle> {
  const locale = resolveSupportedLocale(language);
  const base = await COMMON_RESOURCE_LOADERS.en();
  if (locale === 'en') return base.default;

  const regional = await COMMON_RESOURCE_LOADERS[locale]();
  return mergeResourceBundles(base.default, regional.default);
}
